import { type FC, useState } from 'react'
import classNames from 'classnames'
import { Form } from 'react-bootstrap'
import { Modal } from '../../../components/Modal/Modal'
import { Button } from '../../../components/Button/Button'
import { IntegrationIcon } from '../../../components/IntegrationIcon'
import { INTEGRATION_DISPLAY_NAME_MAP, type IntegrationServiceId } from '../../../common/integrations'
import { isIntakeReady } from '../domain/missingFields'
import { accountHeading, flowCopy, itemCopy } from '../descriptor/stepCopy'
import { AccountTypeFields } from './AccountTypeFields'
import { LoadingScreen } from '../steps/LoadingScreen'
import { ErrorText } from '../steps/flowPrimitives'
import { type ResolvedCatalog } from '../hooks/useAccountTypeCatalog'
import { createEmptyAccount, type ContactOption, type WizardAccount } from '../domain/types'
import styles from '../AccountOpeningWizard.module.scss'

interface AccountIntakeModalProps {
  show: boolean
  connectors: IntegrationServiceId[]
  connector: IntegrationServiceId | null
  catalog: ResolvedCatalog
  initialAccounts: WizardAccount[]
  contactOptions: ContactOption[]
  defaultHolderName?: string | null
  defaultHolderContactId?: string | null
  onConnectorChange: (connector: IntegrationServiceId) => void
  onCancel: () => void
  onContinue: (accounts: WizardAccount[]) => void
}

export const AccountIntakeModal: FC<AccountIntakeModalProps> = ({
  show,
  connectors,
  connector,
  catalog,
  initialAccounts,
  contactOptions,
  defaultHolderName,
  defaultHolderContactId,
  onConnectorChange,
  onCancel,
  onContinue,
}) => {
  const { descriptor, accountTypes, selectors, isLoadingAccountTypes, accountTypesFailed, selectorsFailed, descriptorFailed } = catalog
  const copy = flowCopy(descriptor)
  const items = itemCopy(descriptor)
  const [accounts, setAccounts] = useState<WizardAccount[]>(initialAccounts)

  const canContinue = !!connector && accounts.length > 0 && accounts.every(isIntakeReady)

  const handleConnectorChange = (chosen: IntegrationServiceId) => {
    if (chosen === connector) {
      return
    }
    setAccounts([createEmptyAccount()])
    onConnectorChange(chosen)
  }

  const updateAccount = (updated: WizardAccount) => {
    setAccounts((previous) => previous.map((account) => (account.accountRefId === updated.accountRefId ? updated : account)))
  }

  const removeAccount = (accountRefId: string) => {
    setAccounts((previous) => previous.filter((account) => account.accountRefId !== accountRefId))
  }

  const body = isLoadingAccountTypes ? (
    <LoadingScreen message='Loading account types' compact />
  ) : (
    <div className='d-flex flex-column zocks-gap-1.5'>
      <div className='d-flex flex-column zocks-gap-0.5'>
        <p className='paragraph-medium medium mb-0'>{copy.custodianLabel}</p>
        <div className='d-flex flex-wrap zocks-gap-0.5'>
          {connectors.map((serviceId) => (
            <Form.Check
              key={serviceId}
              type='radio'
              className='d-flex align-items-center zocks-gap-0.5 border zocks-border-color-gray-300 rounded-2 zocks-px-1 zocks-py-0.5 align-self-start mb-0 ps-0'
            >
              <Form.Check.Input
                type='radio'
                id={`account-opening-custodian-${serviceId}`}
                name='account-opening-custodian'
                className='mt-0 ms-0 flex-shrink-0'
                checked={serviceId === connector}
                readOnly={connectors.length === 1}
                onChange={() => handleConnectorChange(serviceId)}
              />
              <Form.Check.Label htmlFor={`account-opening-custodian-${serviceId}`} className='d-flex align-items-center zocks-gap-0.5 mb-0'>
                <IntegrationIcon serviceId={serviceId} width={32} height={32} />
                <span className='paragraph-medium medium'>{INTEGRATION_DISPLAY_NAME_MAP[serviceId]}</span>
              </Form.Check.Label>
            </Form.Check>
          ))}
        </div>
      </div>
      {descriptorFailed ? (
        <ErrorText>The account-opening flow could not be loaded. Close this and try again — an application cannot be started without it.</ErrorText>
      ) : accountTypesFailed || selectorsFailed ? (
        <ErrorText>The list of account types could not be loaded. Close this and try again — an application cannot be started without it.</ErrorText>
      ) : null}
      {!descriptorFailed && !accountTypesFailed && !selectorsFailed && (accountTypes.length === 0 || selectors.length === 0) ? (
        <p className='paragraph-small zocks-text-warning-text mb-0'>{copy.noAccountTypes}</p>
      ) : null}
      {accounts.map((account, index) => (
        <div key={account.accountRefId} className={classNames('d-flex flex-column zocks-gap-1', { 'border-top pt-4': index > 0 })}>
          <div className='d-flex align-items-center justify-content-between'>
            <h5 className='mb-0'>{accountHeading(index, false, items)}</h5>
            {accounts.length > 1 ? (
              <Button variant='secondary' aria-label='Remove account' onClick={() => removeAccount(account.accountRefId)}>
                <i className='bi bi-trash' />
              </Button>
            ) : null}
          </div>
          <AccountTypeFields
            accountTypes={accountTypes}
            selectors={selectors}
            account={account}
            onChange={updateAccount}
            contactOptions={contactOptions}
            defaultHolderName={defaultHolderName}
            defaultHolderContactId={defaultHolderContactId}
          />
        </div>
      ))}
      <div className='border-top pt-4'>
        <Button
          variant='secondary'
          className='d-flex align-items-center zocks-gap-0.5'
          onClick={() => setAccounts((previous) => [...previous, createEmptyAccount()])}
        >
          <i className='bi bi-plus-circle' />
          Add account
        </Button>
      </div>
    </div>
  )

  return (
    <Modal
      show={show}
      onHide={onCancel}
      size='lg'
      scrollable
      contentClassName={styles.formSurface}
      header={<h5 className='mb-0'>{copy.intakeTitle}</h5>}
      body={body}
      showFooterActions
      footerPrimaryActionTitle='Continue'
      footerPrimaryActionDisabled={!canContinue || isLoadingAccountTypes}
      onFooterPrimaryAction={() => onContinue(accounts)}
      onFooterSecondaryAction={onCancel}
    />
  )
}
